import EnterLayout from 'components/enter2026/EnterLayout';
import MainLayout from 'components/layout/MainLayout';
import TealCircleTicket from '../../components/svg/TealCircleTicket.svg';
import { GetServerSideProps } from 'next';
import { tryApi } from 'utils/api/common';
import { drawApiClient } from 'utils/apiClient';
import HttpStatusCode from 'utils/HttpStatusCode';
import { DaymadeDrawTypePlanCode, MainDrawTypeCode } from 'constants/Draw';
import EnterDrawCountdown from 'components/enter2026/EnterDrawCountdown';
import ImageWithFallback from 'components/ImageWithFallback';
import styles from './1.module.css';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import { useEnterStore } from 'hooks/stores/useEnterStore';
import { observer } from 'mobx-react-lite';
import { formattedPriceStringFromPence, singularOrPlural } from 'utils/common';
import { useDrawStore } from 'hooks/stores/useDrawStore';
import RadioToggleButtonFlexContainer from 'components/RadioToggleButtonFlexContainer';
import RadioToggleButton from 'components/RadioToggleButton';
import NewSelectEntriesSection from 'components/enter/NewSelectEntriesSection';
import { EntryMethod, EntryMethodDetails } from 'components/enter/EntryMethod';
import ExpandableOrderSummary from 'components/enter2026/ExpandableOrderSummary';
import RoundedButton from 'components/RoundedButton';
import TrustPilotTwoLinesWidget from 'components/TrustPilotTwoLinesWidget';
import { validEntries } from 'utils/LottoEntryValidator';
import { useUIStore } from 'hooks/stores/useUIStore';
import { useEnterFlowPreconditionCheck } from 'hooks/enter/useEnterFlowPreconditionCheck';

interface EnterNowStep1Props {
  nextDrawDateIsoString: string | null | undefined;
}

const changeOfWinningByEntryCount: Record<string, number> = {
  '1': 34,
  '2': 57,
  '3': 71,
  '4': 81,
};

const EnterNowStep1 = observer((props: EnterNowStep1Props) => {
  const uiStore = useUIStore();
  const enterStore = useEnterStore();
  const drawStore = useDrawStore();
  const {
    entryMethod,
    lines,
    setLines,
    setLineNumber,
    setLineBonusNumber,
    generateLineRandomNumbers,
  } = enterStore;

  useEffect(() => {
    // Fix weirdness when transitioning from free enter flow to normal enter flow
    if (enterStore.plan?.code === DaymadeDrawTypePlanCode.FREE) {
      enterStore.setEntryMethod(EntryMethod.PremiumSubscription);
    }
  }, [enterStore.plan?.code]);
  useEnterFlowPreconditionCheck();

  const entryCounts = Object.entries(enterStore.plan?.priceTable[0].GBP || []);

  return (
    <MainLayout limitItemWidths>
      <EnterLayout
        sidePanel={
          <>
            <EnterDrawCountdown
              date={
                props.nextDrawDateIsoString ??
                drawStore.upcomingDrawDate?.toISOString()
              }
            />
            <div className={styles.winnerImage}>
              <ImageWithFallback
                src="https://daymade-website-assets.s3.eu-west-1.amazonaws.com/2026/enter/EnterStep1.png"
                alt="Photo of winner Harriet G, who won a Japan trip for 2 worth £25,000"
                layout="responsive"
                width="936"
                height="1072"
              />
            </div>
          </>
        }
        sidePanelBackground={'TEAL'}
        stepIcon={<TealCircleTicket />}
        stepName="Lucky Numbers"
        stepIntroText="The more numbers you match correctly, the bigger the prize you can choose."
        stepIndex={1}
        stepTooltipContent={
          <div>
            <p>
              Every entry comes with 5 lucky numbers. They are comprised of{' '}
              <strong>4 main numbers</strong> ranging from <strong>1-55</strong>
              , and <strong>1 bonus number</strong> between{' '}
              <strong>1-10</strong>.
            </p>
            <p>
              These numbers will be entered into the draw every week. The more
              numbers you match correctly, the bigger the prize you can choose
              from.
            </p>
            <p>You can change them at any time from your Account.</p>
          </div>
        }
      >
        <div className={styles.entryCountPicker + ' desktopOnly'}>
          <RadioToggleButtonFlexContainer>
            {entryCounts.map(([min_number_entries, price_per_entry]) => (
              <RadioToggleButton
                key={min_number_entries}
                radioGroupName="entryCount"
                value={min_number_entries}
                selectedValue={enterStore.num_entries + ''}
                onChange={() => enterStore.setNumEntries(+min_number_entries)}
                badgeText={min_number_entries === '2' ? 'Popular' : undefined}
                text={
                  min_number_entries +
                  ' ' +
                  singularOrPlural(min_number_entries, ' ENTRY', ' ENTRIES')
                }
                subtext={formattedPriceStringFromPence(price_per_entry)}
                displayStyle="TICKET"
              />
            ))}
          </RadioToggleButtonFlexContainer>
        </div>
        <div className={'aboutUsTypographyBodySmall ' + styles.drawFacts}>
          {drawStore.winnersLastWeek ? (
            <p>
              <strong>
                🎉 {drawStore.winnersLastWeek.toLocaleString('en-GB')}{' '}
                {singularOrPlural(drawStore.winnersLastWeek, 'win', 'wins')}{' '}
                last week
              </strong>
            </p>
          ) : null}
          <p>
            <strong>
              🥳 {changeOfWinningByEntryCount[enterStore.num_entries]}% chance
              to win
            </strong>
          </p>
        </div>
        <div className={styles.entriesContainer}>
          <div className={styles.entriesHeading + ' desktopOnly'}>
            <h3 className="aboutUsTypographyHeaderMedium">
              Your Lucky Numbers
            </h3>
          </div>
          <div className={styles.entryCountTabBar}>
            {entryCounts.map(([min_number_entries, price_per_entry]) => (
              <button
                key={min_number_entries}
                className={
                  styles.tabBarButton +
                  ' ' +
                  (enterStore.num_entries === +min_number_entries
                    ? styles.tabBarButtonSelected
                    : '')
                }
                onClick={() => enterStore.setNumEntries(+min_number_entries)}
              >
                {min_number_entries}{' '}
                {singularOrPlural(min_number_entries, ' entry', ' entries')}{' '}
                <span className={styles.tabBarSubtext}>
                  {formattedPriceStringFromPence(price_per_entry)}
                </span>
                {min_number_entries === '2' && (
                  <span className={styles.tabBarBadge}>
                    <span>🔥 POPULAR 🔥</span>
                  </span>
                )}
              </button>
            ))}
          </div>

          <NewSelectEntriesSection
            lines={lines}
            setLines={setLines}
            setLineNumber={setLineNumber}
            setLineBonusNumber={setLineBonusNumber}
            generateLineRandomNumbers={generateLineRandomNumbers}
            glowColor={EntryMethodDetails[entryMethod]?.underlineGlowColor}
            noBorder
            locked={false}
            loading={false}
          />
        </div>
        <ExpandableOrderSummary showDays={false} />
        <RoundedButton
          rebrandDm2025
          fullWidth
          href="/enternow/3"
          onClick={(e) => {
            if (
              !validEntries(
                lines,
                drawStore.numberSetup.main,
                drawStore.numberSetup.bonus,
              )
            ) {
              uiStore.displayToast({
                title: 'You cannot have duplicate entries in a draw.',
                timeout: 3500,
              });
              e.preventDefault();
            }
          }}
        >
          Continue
        </RoundedButton>
        <TrustPilotTwoLinesWidget />
      </EnterLayout>
    </MainLayout>
  );
});

export default EnterNowStep1;

export const getServerSideProps: GetServerSideProps<
  EnterNowStep1Props
> = async (_ctx) => {
  const [resp] = await Promise.all([
    tryApi(
      drawApiClient.public.getDrawTypeCached({
        params: { draw_code: MainDrawTypeCode },
      }),
    ),
  ]);

  let nextDrawDate: string | null = null;

  if (resp.status === HttpStatusCode.OK) {
    if (resp.body.next_draw?.drawDate) {
      nextDrawDate = new Date(resp.body.next_draw.drawDate).toISOString();
    }
  }

  return {
    props: {
      nextDrawDateIsoString: nextDrawDate,
    },
  };
};
